// NotificationPanel — dropdown panel with notification list, tabs, and actions.
//
// Renders either inline (header variant, position: absolute relative to parent)
// or via portal (sidebar variant, position: fixed via inline styles).
//
// Panel layout:
//   Header (title + markAllRead + clearAll + close)
//   Tab bar (all / study / system / account)
//   Body (notification list or empty state)

import { useCallback } from 'react';
import { Bell, X, CheckCheck, Trash2, Check, Info, AlertTriangle, AlertOctagon } from 'lucide-react';
import { useT, useLocale } from '../i18n/index.js';
import { formatRelativeTime } from '../lib/relative-time.js';
import { useNavigate } from 'react-router-dom';
import type { AppNotification, MessageFilter } from '@examforge/shared';
import type { PanelPosition } from './use-notification-position.js';

// ── Level icon map (Lucide) ──────────────────────────────────────────────

const LEVEL_ICON = {
  success: Check,
  info: Info,
  warning: AlertTriangle,
  error: AlertOctagon,
} as const;

// ── Tab definition ───────────────────────────────────────────────────────

interface TabSpec {
  key: MessageFilter;
  labelKey: string;
}

const TABS: readonly TabSpec[] = [
  { key: 'all', labelKey: 'notif.tabAll' },
  { key: 'regen', labelKey: 'notif.tabStudy' },
  { key: 'system', labelKey: 'notif.tabSystem' },
  { key: 'account', labelKey: 'notif.tabAccount' },
];

// ── Relative time helper ─────────────────────────────────────────────────
// 19BM-D Phase 6b: extracted to lib/relative-time.ts (was duplicated in
// message-center / notification-panel / history-view).

// ── Props ────────────────────────────────────────────────────────────────

export interface NotificationPanelProps {
  notifications: AppNotification[];
  unreadCount: number;
  activeTab: MessageFilter;
  onTabChange: (tab: MessageFilter) => void;
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  onDismiss: (id: string) => void;
  onClearAll: () => void;
  onClose: () => void;
  panelRef: React.RefObject<HTMLDivElement | null>;
  position: PanelPosition | null;
  variant: 'header' | 'sidebar';
}

// ── Component ────────────────────────────────────────────────────────────

export function NotificationPanel({
  notifications,
  unreadCount,
  activeTab,
  onTabChange,
  onMarkRead,
  onMarkAllRead,
  onDismiss,
  onClearAll,
  onClose,
  panelRef,
  position,
  variant,
}: NotificationPanelProps) {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();

  // Filter notifications by active tab.
  // 'regen' tab includes both 'regen' and 'ingest' sources (both study-related).
  const filtered = activeTab === 'all'
    ? notifications
    : activeTab === 'regen'
      ? notifications.filter((n) => n.source === 'regen' || n.source === 'ingest')
      : notifications.filter((n) => n.source === activeTab);

  const handleAction = useCallback(
    (n: AppNotification) => {
      onMarkRead(n.id);
      if (n.action?.onClick) {
        n.action.onClick();
      } else if (n.action?.to) {
        navigate(n.action.to);
      }
      onClose();
    },
    [onMarkRead, navigate, onClose],
  );

  // Build style for fixed positioning (portal / sidebar variant).
  // Sidebar: bottom-aligned, opens to the right (panel's bottom-left touches bell).
  // Header: top-aligned, opens below the bell.
  const style: React.CSSProperties =
    variant === 'sidebar' && position
      ? {
          position: 'fixed',
          top: 'auto',       // override CSS .notif-panel top (would be 100vh+8px under fixed)
          right: 'auto',     // override CSS .notif-panel right (-8px)
          bottom: position.bottom,
          left: position.left,
          maxHeight: position.maxHeight,
          zIndex: 1000,
        }
      : {};

  const panel = (
    <div
      className={`notif-panel${variant === 'sidebar' ? ' notif-panel--fixed' : ''}`}
      ref={panelRef}
      role="dialog"
      aria-label={t('notif.centerTitle')}
      style={style}
    >
      {/* ── Header ────────────────────────────────────────────────── */}
      <div className="notif-panel-header">
        <h3 className="notif-panel-title">{t('notif.centerTitle')}</h3>
        <div className="notif-panel-actions">
          {unreadCount > 0 && (
            <button
              type="button"
              className="notif-panel-action-btn"
              onClick={onMarkAllRead}
              title={t('notif.markAllRead')}
            >
              <CheckCheck size={14} aria-hidden="true" />
            </button>
          )}
          {notifications.length > 0 && (
            <button
              type="button"
              className="notif-panel-action-btn"
              onClick={onClearAll}
              title={t('notif.clearAll')}
            >
              <Trash2 size={14} aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            className="notif-panel-close-btn"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* ── Tab bar ───────────────────────────────────────────────── */}
      <div className="notif-panel-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`notif-panel-tab${activeTab === tab.key ? ' is-active' : ''}`}
            onClick={() => onTabChange(tab.key)}
          >
            {t(tab.labelKey as never)}
          </button>
        ))}
      </div>

      {/* ── Body ──────────────────────────────────────────────────── */}
      <div className="notif-panel-body">
        {filtered.length === 0 ? (
          <div className="notif-empty">
            <Bell size={24} aria-hidden="true" className="notif-empty-icon" />
            <span className="notif-empty-text">
              {t(activeTab === 'all' ? 'notif.empty' : 'notif.emptyFiltered')}
            </span>
            {activeTab !== 'all' && (
              <button
                type="button"
                className="notif-empty-reset"
                onClick={() => onTabChange('all')}
              >
                {t('notif.viewAll')}
              </button>
            )}
          </div>
        ) : (
          <ul className="notif-list">
            {filtered.map((n) => (
              <li
                key={n.id}
                className={`notif-item${n.read ? '' : ' notif-item--unread'} notif-item--${n.level}`}
                onClick={() => {
                  if (!n.read) onMarkRead(n.id);
                }}
              >
                <span className="notif-item-icon" aria-hidden="true">
                  {(() => {
                    const Icon = LEVEL_ICON[n.level];
                    return <Icon size={10} aria-hidden="true" />;
                  })()}
                </span>
                <div className="notif-item-body">
                  <span className="notif-item-title">{n.title}</span>
                  {n.message && (
                    <span className="notif-item-msg">{n.message}</span>
                  )}
                  <span className="notif-item-time">
                    {formatRelativeTime(n.timestamp, locale, t)}
                  </span>
                </div>
                <div className="notif-item-tail">
                  {n.action && (
                    <button
                      type="button"
                      className="notif-item-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAction(n);
                      }}
                    >
                      {n.action.label}
                    </button>
                  )}
                  <button
                    type="button"
                    className="notif-item-dismiss-btn"
                    aria-label={t('common.close')}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDismiss(n.id);
                    }}
                  >
                    <X size={11} aria-hidden="true" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  // For sidebar variant, render via portal wrapper inside the parent
  // (the parent — NotificationCenter — handles the actual createPortal call).
  return panel;
}
